import { formatCryptoCurrency, truncateAddress } from '@kwenta/sdk/utils'
import { type FC, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import WithdrawCaret from 'assets/svg/app/caret-down-right-red.svg'
import DepositCaret from 'assets/svg/app/caret-up-right-green.svg'
import ColoredPrice from 'components/ColoredPrice'
import { DesktopOnlyView, MobileOrTabletView } from 'components/Media'
import Table, { TableHeader, TableNoResults } from 'components/Table'
import { TableCell } from 'components/Table/TableBodyRow'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRow } from 'components/layout/flex'
import { blockExplorer } from 'containers/Connector/Connector'
import DashboardHistoryTableFilters from 'sections/dashboard/History/HistoryTableFilters'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import {
	selectAccountFilteredTransfers,
	selectMarginTransfersQueryStatus,
} from 'state/futures/selectors'
import { useAppSelector } from 'state/hooks'
import { FetchStatus } from 'state/types'
import { ExternalLink } from 'styles/common'
import { providerIsCrossMargin } from 'utils/futures'

import MobileTableFilters from '../MobileTrade/UserTabs/TableFilters'
import TimeDisplay from '../Trades/TimeDisplay'
import { DEFAULT_TABLE_PAGE_SIZE } from '../styles'

import UserInfoTableFilters from './TableFilters/TableFilters'
import { FuturesTab } from './UserInfoTabs'

type TransactionHistoryTableProps = {
	filter?: 'dashboard' | 'userInfo'
	pagination?: boolean
	rounded?: boolean
	noBottom?: boolean
}

const TransactionHistoryTable: FC<TransactionHistoryTableProps> = memo(
	({ filter = 'userInfo', pagination = false, rounded = false, noBottom = false }) => {
		const { t } = useTranslation()
		const perpsProvider = useAppSelector(selectPerpsProvider)
		const marginTransfersStatus = useAppSelector(selectMarginTransfersQueryStatus)

		const transactionsHistory = useAppSelector(
			selectAccountFilteredTransfers,
			(a, b) => a.length === b.length
		)

		const isLoading = useMemo(
			() => !transactionsHistory.length && marginTransfersStatus === FetchStatus.Loading,
			[transactionsHistory.length, marginTransfersStatus]
		)

		const columnsDeps = useMemo(() => [transactionsHistory], [transactionsHistory])

		return (
			<Container>
				<DesktopOnlyView>
					<TableContainer>
						{filter === 'userInfo' ? (
							<UserInfoTableFilters tab={FuturesTab.TRANSACTION_HISTORY} showFilterType />
						) : filter === 'dashboard' ? (
							<DashboardHistoryTableFilters />
						) : null}
						<Table
							highlightRowsOnHover
							rounded={rounded}
							noBottom={noBottom}
							showPagination={pagination}
							pageSize={pagination ? DEFAULT_TABLE_PAGE_SIZE : undefined}
							columns={[
								{
									header: () => (
										<TableHeader>{t('futures.market.user.transaction-history.date')}</TableHeader>
									),
									accessorKey: 'timestamp',
									cell: (cellProps) => <TimeDisplay value={cellProps.getValue()} />,
									enableSorting: true,
								},
								{
									header: () => (
										<TableHeader>{t('futures.market.user.transaction-history.type')}</TableHeader>
									),
									accessorKey: 'action',
									cell: (cellProps) => {
										return (
											<TransactionTypeContainer>
												{cellProps.row.original.action === 'deposit' ? (
													<DepositCaret />
												) : (
													<WithdrawCaret />
												)}
												<TransactionType>{cellProps.row.original.action}</TransactionType>
											</TransactionTypeContainer>
										)
									},
									enableSorting: true,
								},
								{
									header: () => (
										<TableHeader>{t('futures.market.user.transaction-history.amount')}</TableHeader>
									),
									accessorKey: 'size',
									cell: (cellProps) => {
										return cellProps.getValue() === 0 ? (
											'-'
										) : (
											<ColoredPrice priceChange={cellProps.getValue() > 0 ? 'up' : 'down'}>
												{cellProps.getValue() > 0 && <Invisible>-</Invisible>}
												{formatCryptoCurrency(cellProps.getValue(), {
													suggestDecimals: true,
												})}
											</ColoredPrice>
										)
									},
									enableSorting: true,
								},
								{
									header: () => (
										<TableHeader>{t('futures.market.user.transaction-history.asset')}</TableHeader>
									),
									accessorKey: 'asset',
									cell: (cellProps) => {
										let asset = 'sUSD'
										if (providerIsCrossMargin(perpsProvider)) {
											asset = cellProps.getValue() === 'sUSD' ? 'USDC' : cellProps.getValue()
										}

										return <Body>{asset}</Body>
									},
									enableSorting: true,
								},
								{
									header: () => (
										<TableHeader>
											{t('futures.market.user.transaction-history.transaction')}
										</TableHeader>
									),
									accessorKey: 'txHash',
									cell: (cellProps) => {
										return (
											<Body>
												<StyledExternalLink href={blockExplorer.txLink(cellProps.getValue())}>
													{truncateAddress(cellProps.getValue())}
												</StyledExternalLink>
											</Body>
										)
									},
								},
							]}
							columnsDeps={columnsDeps}
							data={transactionsHistory}
							isLoading={isLoading}
							noResultsMessage={
								!isLoading ? (
									<TableNoResults>
										{t('futures.market.user.trades.table.no-results')}
									</TableNoResults>
								) : undefined
							}
						/>
					</TableContainer>
				</DesktopOnlyView>

				<MobileOrTabletView>
					<MobileTableContainer>
						{filter === 'userInfo' && <MobileTableFilters showFilters />}
						<MobileTable
							highlightRowsOnHover
							rounded={false}
							noBottom={false}
							autoResetPageIndex={false}
							showPagination={false}
							columns={[
								{
									accessorKey: 'asset',
									cell: (cellProps) => {
										let asset = 'sUSD'
										if (providerIsCrossMargin(perpsProvider)) {
											asset = cellProps.getValue() === 'sUSD' ? 'USDC' : cellProps.getValue()
										}
										return (
											<FlexDivCol>
												<Body>{asset}</Body>
												<TransactionTypeContainer>
													{cellProps.row.original.action === 'deposit' ? (
														<DepositCaret />
													) : (
														<WithdrawCaret />
													)}
													<TransactionType>{cellProps.row.original.action}</TransactionType>
												</TransactionTypeContainer>
											</FlexDivCol>
										)
									},
								},
								{
									accessorKey: 'size',
									cell: (cellProps) => (
										<FlexDivCol>
											<TableHeader>
												{t('futures.market.user.transaction-history.amount')}
											</TableHeader>
											{cellProps.getValue() === 0 ? (
												'-'
											) : (
												<ColoredPrice priceChange={cellProps.getValue() > 0 ? 'up' : 'down'}>
													{formatCryptoCurrency(cellProps.getValue(), {
														suggestDecimals: true,
													})}
												</ColoredPrice>
											)}
										</FlexDivCol>
									),
								},
								{
									accessorKey: 'timestamp',
									cell: (cellProps) => <TimeDisplay value={cellProps.getValue()} />,
								},
								{
									accessorKey: 'txHash',
									cell: (cellProps) => (
										<FlexDivCol>
											<TableHeader>
												{t('futures.market.user.transaction-history.transaction')}
											</TableHeader>
											<Body>
												<StyledExternalLink href={blockExplorer.txLink(cellProps.getValue())}>
													{truncateAddress(cellProps.getValue())}
												</StyledExternalLink>
											</Body>
										</FlexDivCol>
									),
								},
							]}
							columnsDeps={columnsDeps}
							data={transactionsHistory}
							isLoading={isLoading && !transactionsHistory?.length}
							noResultsMessage={
								!isLoading && transactionsHistory?.length === 0 ? (
									<TableNoResults>
										{t('futures.market.user.trades.table.no-results')}
									</TableNoResults>
								) : undefined
							}
						/>
					</MobileTableContainer>
				</MobileOrTabletView>
			</Container>
		)
	}
)

export default TransactionHistoryTable

const Container = styled.div`
	display: flex;
	flex-direction: row;
	flex: 1;
	height: 100%;
`

const MobileTableContainer = styled(FlexDivCol)`
	width: 100%;
`

const MobileTable = styled(Table)`
	.table-row:first-child {
		display: none;
	}

	.table-body {
		max-height: 100%;
	}

	.table-body-row:first-child {
		padding-top: 15px;
	}

	.table-body-row {
		display: grid;
		grid-template-columns: 1fr 1fr;
		grid-template-rows: auto;
		grid-column-gap: 15px;
		grid-row-gap: 10px;
		padding: 15px 0px;
		margin: 0 20px;
	}

	${TableCell}:first-child,
	${TableCell}:last-child {
		padding: 0;
	}
	${TableCell} {
		height: 100%;
		width: 100% !important;
		display: flex;
		align-items: start;
		justify-content: space-between;
	}
	border: none;
` as typeof Table

const TransactionTypeContainer = styled(FlexDivRow)`
	align-items: center;
	justify-content: center;
	gap: 5px;
`

const TransactionType = styled(Body)`
	text-transform: capitalize;
`

const TableContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;

	.table-body-row {
		border-bottom: none;
	}
`
const StyledExternalLink = styled(ExternalLink)`
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	text-decoration: underline;
	&:hover {
		text-decoration: underline;
	}
`
const Invisible = styled.span`
	color: transparent;
`
